import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { ReviewPrepMetrics } from '../../../../hooks/useInternshipReviewPrep';
import { ReflectionData } from './ReflectionStep';
import { TalkingPoint, TalkingPointsResponse } from '../../../../hooks/useTalkingPoints';
import { 
  ChatBubbleLeftRightIcon, 
  ClipboardDocumentListIcon,
  SparklesIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

interface TalkingPointsStepProps {
  metrics: ReviewPrepMetrics;
  reflections: ReflectionData;
  aiGeneratedPoints?: Record<string, TalkingPointsResponse>;
  onNext: (talkingPoints: TalkingPointsData) => void;
  onBack: () => void;
  initialData?: TalkingPointsData | null;
}

export interface TalkingPointsData {
  openingStatement: string;
  strengthHighlights: string[];
  growthExamples: string[];
  challengeNarratives: string[];
  futureCommitments: string[];
  questionsForManager: string[];
  closingThoughts: string;
}

const TalkingPointsStep: React.FC<TalkingPointsStepProps> = ({ 
  metrics, 
  reflections, 
  aiGeneratedPoints,
  onNext, 
  onBack,
  initialData 
}) => {
  const { darkMode } = useDarkMode();
  const [selectedPoints, setSelectedPoints] = useState<Set<string>>(new Set());
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const selectedBgColor = darkMode ? 'bg-blue-900/20' : 'bg-blue-50';
  const selectedBorderColor = darkMode ? 'border-blue-700' : 'border-blue-300';
  
  // Use initial data if provided, otherwise generate talking points based on metrics and reflections
  const talkingPoints = useMemo(() => {
    if (initialData) {
      return initialData;
    }
    
    const points: TalkingPointsData = {
      openingStatement: generateOpeningStatement(metrics, reflections),
      strengthHighlights: generateStrengthHighlights(metrics, reflections),
      growthExamples: generateGrowthExamples(metrics, reflections),
      challengeNarratives: generateChallengeNarratives(reflections),
      futureCommitments: generateFutureCommitments(reflections),
      questionsForManager: generateQuestionsForManager(metrics),
      closingThoughts: generateClosingThoughts(metrics, reflections)
    };
    return points;
  }, [metrics, reflections, initialData]);
  
  const togglePointSelection = (pointId: string) => {
    setSelectedPoints(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pointId)) {
        newSet.delete(pointId);
      } else {
        newSet.add(pointId);
      }
      return newSet;
    });
  };
  
  const handleNext = () => {
    onNext(talkingPoints);
  };
  
  const copyToClipboard = () => {
    const selectedText = generateSelectedText();
    navigator.clipboard.writeText(selectedText);
    // Could add toast notification here
  };
  
  const generateSelectedText = () => {
    let text = '';
    
    // Add AI-generated points first (only evidence-based points can be selected)
    if (aiGeneratedPoints) {
      const hasAIPoints = Array.from(selectedPoints).some(id => id.startsWith('ai-'));
      if (hasAIPoints) {
        text += 'AI-Generated Talking Points:\n\n';
        Object.entries(aiGeneratedPoints).forEach(([rubricItemId, response]) => {
          if (response.type === 'evidence-based' && 'points' in response) {
            response.points.forEach((point, idx) => {
              if (selectedPoints.has(`ai-${rubricItemId}-${idx}`)) {
                text += `[${point.format}]\n${point.fullSuggestion}\n\n`;
              }
            });
          }
        });
        text += '---\n\n';
      }
    }
    
    if (selectedPoints.has('opening')) {
      text += `Opening Statement:\n${talkingPoints.openingStatement}\n\n`;
    }
    
    talkingPoints.strengthHighlights.forEach((point, idx) => {
      if (selectedPoints.has(`strength-${idx}`)) {
        text += `• ${point}\n`;
      }
    });
    
    if (text.includes('•')) text += '\n';
    
    talkingPoints.growthExamples.forEach((point, idx) => {
      if (selectedPoints.has(`growth-${idx}`)) {
        text += `Growth Example:\n${point}\n\n`;
      }
    });
    
    talkingPoints.challengeNarratives.forEach((point, idx) => {
      if (selectedPoints.has(`challenge-${idx}`)) {
        text += `Challenge Overcome:\n${point}\n\n`;
      }
    });
    
    talkingPoints.futureCommitments.forEach((point, idx) => {
      if (selectedPoints.has(`future-${idx}`)) {
        text += `Future Commitment:\n${point}\n\n`;
      }
    });
    
    talkingPoints.questionsForManager.forEach((point, idx) => {
      if (selectedPoints.has(`question-${idx}`)) {
        text += `Question: ${point}\n`;
      }
    });
    
    if (selectedPoints.has('closing')) {
      text += `\nClosing:\n${talkingPoints.closingThoughts}`;
    }
    
    return text.trim();
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-start gap-3">
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
          <div>
            <h3 className={`font-semibold mb-2 ${textColor}`}>Your Talking Points</h3>
            <p className={`text-sm ${mutedTextColor}`}>
              Based on your evidence and reflections, here are structured talking points for your review. 
              Select the ones you want to use and customize as needed.
            </p>
          </div>
        </div>
      </div>
      
      {/* AI-Generated Points Section */}
      {aiGeneratedPoints && Object.keys(aiGeneratedPoints).length > 0 && (
        <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
          <div className="flex items-start gap-3 mb-4">
            <SparklesIcon className="w-6 h-6 text-purple-500 flex-shrink-0" />
            <div>
              <h4 className={`font-semibold mb-1 ${textColor}`}>AI-Generated Content</h4>
              <p className={`text-sm ${mutedTextColor}`}>
                Generated suggestions based on your evidence and rubric criteria.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            {Object.entries(aiGeneratedPoints).map(([rubricItemId, response]) => {
              const rubricItem = metrics.rubricCoverage.find(r => r.rubricItemId === rubricItemId);
              const isGuidance = response.type === 'guidance';
              
              return (
                <div key={rubricItemId} className={`p-4 rounded-md ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} border ${borderColor}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <span className="text-lg flex-shrink-0">
                      {isGuidance ? '💡' : '✨'}
                    </span>
                    <div className="flex-1">
                      <p className={`text-xs font-medium ${mutedTextColor}`}>
                        {rubricItem?.category} - {rubricItem?.description}
                      </p>
                      <p className={`text-xs ${mutedTextColor} mt-1`}>
                        {response.disclaimer}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {isGuidance && 'suggestions' in response ? (
                      // Guidance content (non-selectable)
                      <div className="space-y-3">
                        {response.suggestions.map((suggestion, idx) => (
                          <div
                            key={`guidance-${rubricItemId}-${idx}`}
                            className={`p-3 rounded-md border ${borderColor} ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-sm flex-shrink-0">
                                {suggestion.promptType === 'reflection-question' ? '🤔' : 
                                 suggestion.promptType === 'evidence-prompt' ? '📝' : '✅'}
                              </span>
                              <div className="flex-1">
                                <p className={`text-xs font-medium ${mutedTextColor} mb-1`}>
                                  {suggestion.promptType === 'reflection-question' ? 'Reflection Question' :
                                   suggestion.promptType === 'evidence-prompt' ? 'Evidence Prompt' : 'Action Item'}
                                </p>
                                <p className={`text-sm ${textColor}`}>{suggestion.content}</p>
                                {suggestion.examples && suggestion.examples.length > 0 && (
                                  <div className="mt-2">
                                    <p className={`text-xs ${mutedTextColor} mb-1`}>Examples:</p>
                                    <ul className="list-disc list-inside">
                                      {suggestion.examples.map((example, exIdx) => (
                                        <li key={exIdx} className={`text-xs ${mutedTextColor}`}>{example}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : response.type === 'evidence-based' && 'points' in response ? (
                      // Evidence-based talking points (selectable)
                      response.points.map((point, idx) => (
                        <motion.div
                          key={`ai-${rubricItemId}-${idx}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className={`p-3 rounded-md border ${
                            selectedPoints.has(`ai-${rubricItemId}-${idx}`) ? selectedBorderColor : borderColor
                          } ${selectedPoints.has(`ai-${rubricItemId}-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
                          onClick={() => togglePointSelection(`ai-${rubricItemId}-${idx}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${textColor} mb-1`}>{point.format}</p>
                              <p className={`text-sm ${textColor}`}>{point.fullSuggestion}</p>
                            </div>
                            <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                              selectedPoints.has(`ai-${rubricItemId}-${idx}`) ? 'text-blue-500' : mutedTextColor
                            }`} />
                          </div>
                        </motion.div>
                      ))
                    ) : null}
                  </div>
                  
                  {/* Tips section */}
                  {response.tips && response.tips.length > 0 && (
                    <div className={`mt-3 p-2 rounded ${darkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                      <p className={`text-xs font-medium ${mutedTextColor} mb-1`}>Tips:</p>
                      <ul className="list-disc list-inside">
                        {response.tips.map((tip, tipIdx) => (
                          <li key={tipIdx} className={`text-xs ${mutedTextColor}`}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Opening Statement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${cardBgColor} rounded-lg border ${
          selectedPoints.has('opening') ? selectedBorderColor : borderColor
        } ${selectedPoints.has('opening') ? selectedBgColor : ''} p-6 cursor-pointer transition-all`}
        onClick={() => togglePointSelection('opening')}
      >
        <div className="flex items-start justify-between mb-3">
          <h4 className={`font-semibold ${textColor}`}>🎯 Opening Statement</h4>
          <CheckCircleIcon className={`w-5 h-5 ${
            selectedPoints.has('opening') ? 'text-blue-500' : mutedTextColor
          }`} />
        </div>
        <p className={`text-sm ${textColor} leading-relaxed`}>
          {talkingPoints.openingStatement}
        </p>
      </motion.div>
      
      {/* Key Strengths */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <h4 className={`font-semibold mb-4 ${textColor}`}>💪 Key Strengths to Highlight</h4>
        <div className="space-y-3">
          {talkingPoints.strengthHighlights.map((point, idx) => (
            <motion.div
              key={`strength-${idx}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-md border ${
                selectedPoints.has(`strength-${idx}`) ? selectedBorderColor : borderColor
              } ${selectedPoints.has(`strength-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all`}
              onClick={() => togglePointSelection(`strength-${idx}`)}
            >
              <div className="flex items-start justify-between">
                <p className={`text-sm ${textColor} flex-1`}>{point}</p>
                <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                  selectedPoints.has(`strength-${idx}`) ? 'text-blue-500' : mutedTextColor
                }`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Growth Examples */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <h4 className={`font-semibold mb-4 ${textColor}`}>📈 Growth & Learning Examples</h4>
        <div className="space-y-3">
          {talkingPoints.growthExamples.map((point, idx) => (
            <motion.div
              key={`growth-${idx}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-md border ${
                selectedPoints.has(`growth-${idx}`) ? selectedBorderColor : borderColor
              } ${selectedPoints.has(`growth-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all`}
              onClick={() => togglePointSelection(`growth-${idx}`)}
            >
              <div className="flex items-start justify-between">
                <p className={`text-sm ${textColor} flex-1`}>{point}</p>
                <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                  selectedPoints.has(`growth-${idx}`) ? 'text-blue-500' : mutedTextColor
                }`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Challenge Stories */}
      {talkingPoints.challengeNarratives.length > 0 && (
        <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
          <h4 className={`font-semibold mb-4 ${textColor}`}>🏔️ Challenges Overcome</h4>
          <div className="space-y-3">
            {talkingPoints.challengeNarratives.map((point, idx) => (
              <motion.div
                key={`challenge-${idx}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`p-4 rounded-md border ${
                  selectedPoints.has(`challenge-${idx}`) ? selectedBorderColor : borderColor
                } ${selectedPoints.has(`challenge-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all`}
                onClick={() => togglePointSelection(`challenge-${idx}`)}
              >
                <div className="flex items-start justify-between">
                  <p className={`text-sm ${textColor} flex-1`}>{point}</p>
                  <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                    selectedPoints.has(`challenge-${idx}`) ? 'text-blue-500' : mutedTextColor
                  }`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      
      {/* Future Commitments */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <h4 className={`font-semibold mb-4 ${textColor}`}>🚀 Future Commitments</h4>
        <div className="space-y-3">
          {talkingPoints.futureCommitments.map((point, idx) => (
            <motion.div
              key={`future-${idx}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-md border ${
                selectedPoints.has(`future-${idx}`) ? selectedBorderColor : borderColor
              } ${selectedPoints.has(`future-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all`}
              onClick={() => togglePointSelection(`future-${idx}`)}
            >
              <div className="flex items-start justify-between">
                <p className={`text-sm ${textColor} flex-1`}>{point}</p>
                <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                  selectedPoints.has(`future-${idx}`) ? 'text-blue-500' : mutedTextColor
                }`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Questions for Manager */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <h4 className={`font-semibold mb-4 ${textColor}`}>❓ Questions for Your Manager</h4>
        <div className="space-y-3">
          {talkingPoints.questionsForManager.map((point, idx) => (
            <motion.div
              key={`question-${idx}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-md border ${
                selectedPoints.has(`question-${idx}`) ? selectedBorderColor : borderColor
              } ${selectedPoints.has(`question-${idx}`) ? selectedBgColor : ''} cursor-pointer transition-all`}
              onClick={() => togglePointSelection(`question-${idx}`)}
            >
              <div className="flex items-start justify-between">
                <p className={`text-sm ${textColor} flex-1`}>{point}</p>
                <CheckCircleIcon className={`w-5 h-5 ml-3 flex-shrink-0 ${
                  selectedPoints.has(`question-${idx}`) ? 'text-blue-500' : mutedTextColor
                }`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Closing Thoughts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${cardBgColor} rounded-lg border ${
          selectedPoints.has('closing') ? selectedBorderColor : borderColor
        } ${selectedPoints.has('closing') ? selectedBgColor : ''} p-6 cursor-pointer transition-all`}
        onClick={() => togglePointSelection('closing')}
      >
        <div className="flex items-start justify-between mb-3">
          <h4 className={`font-semibold ${textColor}`}>🎬 Closing Thoughts</h4>
          <CheckCircleIcon className={`w-5 h-5 ${
            selectedPoints.has('closing') ? 'text-blue-500' : mutedTextColor
          }`} />
        </div>
        <p className={`text-sm ${textColor} leading-relaxed`}>
          {talkingPoints.closingThoughts}
        </p>
      </motion.div>
      
      {/* Action Bar */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-4 sticky bottom-0`}>
        <div className="flex items-center justify-between">
          <div className={`text-sm ${mutedTextColor}`}>
            <ClipboardDocumentListIcon className="w-4 h-4 inline mr-1" />
            {selectedPoints.size} talking points selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={copyToClipboard}
              disabled={selectedPoints.size === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                selectedPoints.size > 0
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <DocumentDuplicateIcon className="w-4 h-4" />
              Copy Selected
            </button>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }`}
        >
          Back to Reflection
        </button>
        
        <button
          onClick={handleNext}
          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <CheckCircleIcon className="w-5 h-5" />
          Complete Preparation
        </button>
      </div>
    </div>
  );
};

// Helper functions to generate talking points
function generateOpeningStatement(metrics: ReviewPrepMetrics, reflections: ReflectionData): string {
  const coverage = metrics.overallCoverageScore;
  const activity = metrics.recentActivityLevel;
  
  if (coverage >= 80 && activity === 'high') {
    return `I'm excited to discuss my internship experience. I've documented ${metrics.totalCards} achievements across all evaluation areas, maintaining consistent progress throughout. ${reflections.biggestWin ? `My standout accomplishment was ${reflections.biggestWin.split('.')[0]}.` : ''}`;
  } else if (coverage >= 60) {
    return `Thank you for this opportunity to reflect on my internship journey. I've captured ${metrics.totalCards} key experiences that demonstrate my growth and contributions. ${reflections.biggestWin ? `I'm particularly proud of ${reflections.biggestWin.split('.')[0]}.` : ''}`;
  } else {
    return `I appreciate this chance to share my internship experience. While I've focused on quality over quantity in my documentation, I've gained valuable insights and made meaningful contributions to the team.`;
  }
}

function generateStrengthHighlights(metrics: ReviewPrepMetrics, reflections: ReflectionData): string[] {
  const strengths: string[] = [];
  
  // Add strengths based on coverage
  const strongAreas = metrics.rubricCoverage
    .filter(item => item.status === 'strong')
    .slice(0, 3);
  
  strongAreas.forEach(area => {
    strengths.push(`Demonstrated consistent excellence in ${area.category} with ${area.evidenceCount} documented examples`);
  });
  
  // Add from reflections
  if (reflections.keyLearnings) {
    const learnings = reflections.keyLearnings.split('\n').filter(l => l.trim());
    if (learnings.length > 0) {
      strengths.push(`Acquired key skills including ${learnings[0].replace(/^[•\-]\s*/, '')}`);
    }
  }
  
  // Activity-based strength
  if (metrics.cardsPerWeek >= 2) {
    strengths.push(`Maintained consistent documentation habit with ${metrics.cardsPerWeek.toFixed(1)} updates per week`);
  }
  
  return strengths.slice(0, 4);
}

function generateGrowthExamples(metrics: ReviewPrepMetrics, reflections: ReflectionData): string[] {
  const examples: string[] = [];
  
  // From reflections
  if (reflections.growth) {
    const growthPoints = reflections.growth.split('.').filter(p => p.trim().length > 20);
    examples.push(...growthPoints.slice(0, 2).map(p => p.trim()));
  }
  
  // From improving trends
  const improvingAreas = metrics.rubricCoverage
    .filter(item => item.trend === 'improving')
    .slice(0, 2);
  
  improvingAreas.forEach(area => {
    examples.push(`Showed continuous improvement in ${area.category} with recent evidence demonstrating advanced understanding`);
  });
  
  return examples.slice(0, 3);
}

function generateChallengeNarratives(reflections: ReflectionData): string[] {
  const narratives: string[] = [];
  
  if (reflections.challenges) {
    const challenges = reflections.challenges.split('.').filter(c => c.trim().length > 20);
    narratives.push(...challenges.slice(0, 2).map(c => c.trim()));
  }
  
  return narratives;
}

function generateFutureCommitments(reflections: ReflectionData): string[] {
  const commitments: string[] = [];
  
  if (reflections.futureGoals) {
    const goals = reflections.futureGoals.split('\n').filter(g => g.trim());
    commitments.push(...goals.slice(0, 3).map(g => g.replace(/^[•\-]\s*/, '').trim()));
  }
  
  // Add generic commitments if needed
  if (commitments.length < 2) {
    commitments.push('Continue developing technical skills through hands-on projects');
    commitments.push('Seek mentorship opportunities to accelerate professional growth');
  }
  
  return commitments.slice(0, 3);
}

function generateQuestionsForManager(metrics: ReviewPrepMetrics): string[] {
  const questions: string[] = [];
  
  // Based on weak areas
  const weakAreas = metrics.rubricCoverage
    .filter(item => item.status === 'weak' || item.status === 'critical')
    .slice(0, 2);
  
  weakAreas.forEach(area => {
    questions.push(`What specific improvements would you suggest for my ${area.category} skills?`);
  });
  
  // General development questions
  questions.push('Based on my performance, what career paths would you recommend I explore?');
  questions.push('What skills should I prioritize developing to excel in this field?');
  
  if (metrics.daysUntilEnd <= 14) {
    questions.push('Are there any final projects or knowledge transfer tasks you\'d like me to complete?');
  }
  
  return questions.slice(0, 4);
}

function generateClosingThoughts(metrics: ReviewPrepMetrics, reflections: ReflectionData): string {
  if (reflections.proudestMoment) {
    return `This internship has been transformative for my career development. ${reflections.proudestMoment.split('.')[0]}. I'm grateful for the mentorship and opportunities provided, and I'm committed to applying these learnings in my future endeavors.`;
  }
  
  return `I'm deeply grateful for this internship experience and the trust you've placed in me. The skills and insights I've gained here will be foundational to my career. I look forward to staying connected and continuing to grow from this experience.`;
}

export default TalkingPointsStep;